import { ActionContext as VuexActionContext, Store } from 'vuex'
import { Namespace } from '@/store/Namespaces'
import {
  ContactOptions,
  ContactAssemblyOptions,
  ContactFormState,
  ContactFormProperty,
  ExistingPropertyNetworkOptions
} from '@/types/landlord-order.types'

/* istanbul ignore file */

const namespace = (fragment: string) => `${Namespace.LandlordOrder}/${Namespace.LandlordOrderContact}/${fragment}`

interface UpdateFormField {
  field: ContactFormProperty;
  value: string;
}

enum Property {
  ContactOption = 'contactOption',
  AssemblyOption = 'assemblyOption',
  ExistingPropertyNetworkOption = 'existingPropertyNetworkOption',
  Form = 'form',
  IsFormDirty = 'isFormDirty'
}

enum Setter {
  ContactOption = 'setContactOption',
  AssemblyOption = 'setAssemblyOption',
  ExistingPropertyNetworkOption = 'setExistingPropertyNetworkOption',
  FormField = 'setFormField',
  Form = 'setForm',
  UpdateIsFormDirty = 'updateIsFormDirty'
}

enum Action {
  UpdateContactOption = 'updateContactOption',
  UpdateAssemblyOption = 'updateAssemblyOption',
  UpdateExistingPropertyNetworkOption = 'updateExistingPropertyNetworkOption',
  UpdateFormField = 'updateFormField',
  ResetContactForm = 'resetContactForm'
}

enum Getter {
  ContactOption = 'getContactOption',
  AssemblyOption = 'getAssemblyOption',
  ExistingPropertyNetworkOption = 'getExistingPropertyNetworkOption',
  FormField = 'getFormField',
  Form = 'getForm'
}

interface State {
  [Property.ContactOption]: ContactOptions;
  [Property.AssemblyOption]: ContactAssemblyOptions;
  [Property.ExistingPropertyNetworkOption]: ExistingPropertyNetworkOptions;
  [Property.Form]: ContactFormState;
  [Property.IsFormDirty]: boolean;
}

export type ActionContext = VuexActionContext<State, {}>

export const getContactOption = (store: Store<{}>): ContactOptions => {
  return store.getters[namespace(Getter.ContactOption)]
}

export const updateContactOption = (store: Store<{}>, payload: ContactOptions): Promise<void> => {
  return store.dispatch(namespace(Action.UpdateContactOption), payload)
}

export const getAssemblyOption = (store: Store<{}>): ContactAssemblyOptions => {
  return store.getters[namespace(Getter.AssemblyOption)]
}

export const updateAssemblyOption = (store: Store<{}>, payload: ContactAssemblyOptions): Promise<void> => {
  return store.dispatch(namespace(Action.UpdateAssemblyOption), payload)
}

export const getExistingPropertyNetworkOption = (store: Store<{}>): ExistingPropertyNetworkOptions => {
  return store.getters[namespace(Getter.ExistingPropertyNetworkOption)]
}

export const updateExistingPropertyNetworkOption = (store: Store<{}>, payload: ExistingPropertyNetworkOptions): Promise<void> => {
  return store.dispatch(namespace(Action.UpdateExistingPropertyNetworkOption), payload)
}

export const getFormField = (store: Store<{}>, field: ContactFormProperty): string => {
  return store.getters[namespace(Getter.FormField)](field)
}

export const getForm = (store: Store<{}>): ContactFormState => {
  return store.getters[namespace(Getter.Form)]
}

export const updateFormField = (store: Store<{}>, payload: UpdateFormField): Promise<void> => {
  return store.dispatch(namespace(Action.UpdateFormField), payload)
}

export const resetContactForm = (store: Store<{}>): Promise<void> => {
  return store.dispatch(namespace(Action.ResetContactForm))
}

export const getIsFormDirty = (store: Store<State>): boolean => store.getters[namespace(Property.IsFormDirty)]

const initialContactForm = {
  [ContactFormProperty.Name]: '',
  [ContactFormProperty.MobileNumber]: '',
  [ContactFormProperty.Email]: '',

  [ContactFormProperty.TechnicianName]: '',
  [ContactFormProperty.TechnicianMobileNumber]: '',
  [ContactFormProperty.TechnicianEmail]: '',

  [ContactFormProperty.DeliveryName]: '',
  [ContactFormProperty.DeliveryMobileNumber]: '',
  [ContactFormProperty.DeliveryStreetName]: '',
  [ContactFormProperty.DeliveryStreetNumber]: '',
  [ContactFormProperty.DeliveryEntrance]: '',
  [ContactFormProperty.DeliveryCity]: '',

  [ContactFormProperty.MountingTechnicianName]: '',
  [ContactFormProperty.MountingTechnicianMobileNumber]: '',

  [ContactFormProperty.TechnicalDeliveryName]: '',
  [ContactFormProperty.TechnicalDeliveryMobileNumber]: '',
  [ContactFormProperty.TechnicalDeliveryStreetName]: '',
  [ContactFormProperty.TechnicalDeliveryStreetNumber]: '',
  [ContactFormProperty.TechnicalDeliveryEntrance]: '',
  [ContactFormProperty.TechnicalDeliveryCity]: '',

  [ContactFormProperty.DeliveryDate]: '',
  [ContactFormProperty.OtherInformation]: ''
}

const landlordOrderContactModule = {
  namespaced: true,
  state: {
    [Property.ContactOption]: ContactOptions.Same,
    [Property.AssemblyOption]: ContactAssemblyOptions.Mounted,
    [Property.ExistingPropertyNetworkOption]: '',
    [Property.Form]: JSON.parse(JSON.stringify(initialContactForm)),
    [Property.IsFormDirty]: false
  },
  getters: {
    [Getter.ContactOption]: (state: State) => state[Property.ContactOption],
    [Getter.AssemblyOption]: (state: State) => state[Property.AssemblyOption],
    [Getter.ExistingPropertyNetworkOption]: (state: State) => state[Property.ExistingPropertyNetworkOption],
    [Getter.FormField]: (state: State) => (field: ContactFormProperty) => state.form[field],
    [Getter.Form]: (state: State) => state[Property.Form],
    [Property.IsFormDirty]: (state: State): boolean => state[Property.IsFormDirty]
  },
  mutations: {
    [Setter.ContactOption]: (state: State, payload: ContactOptions) => {
      state[Property.ContactOption] = payload
    },
    [Setter.AssemblyOption]: (state: State, payload: ContactAssemblyOptions) => {
      state[Property.AssemblyOption] = payload
    },
    [Setter.ExistingPropertyNetworkOption]: (state: State, payload: ExistingPropertyNetworkOptions) => {
      state[Property.ExistingPropertyNetworkOption] = payload
    },
    [Setter.FormField]: (state: State, payload: UpdateFormField) => state.form[payload.field] = payload.value,
    [Setter.Form]: (state: State, payload: ContactFormState) => state.form = payload,
    [Setter.UpdateIsFormDirty]: (state: State, payload: boolean) => state[Property.IsFormDirty] = payload
  },
  actions: {
    [Action.UpdateContactOption]: ({ commit }: ActionContext, payload: ContactOptions): void => {
      commit(Setter.ContactOption, payload)
    },
    [Action.UpdateAssemblyOption]: ({ commit }: ActionContext, payload: ContactAssemblyOptions): void => {
      commit(Setter.AssemblyOption, payload)
    },
    [Action.UpdateExistingPropertyNetworkOption]: ({ commit }: ActionContext, payload: ExistingPropertyNetworkOptions): void => {
      commit(Setter.ExistingPropertyNetworkOption, payload)
    },
    [Action.UpdateFormField]: ({ commit }: ActionContext, payload: UpdateFormField): void => {
      commit(Setter.FormField, payload)
      commit(Setter.UpdateIsFormDirty, true)
    },
    [Action.ResetContactForm]: ({ commit }: ActionContext): void => {
      commit(Setter.ContactOption, ContactOptions.Same)
      commit(Setter.AssemblyOption, ContactAssemblyOptions.Mounted)
      commit(Setter.Form, JSON.parse(JSON.stringify(initialContactForm)))
      commit(Setter.UpdateIsFormDirty, false)
    }
  }
}

export {
  namespace,
  Property,
  Getter,
  landlordOrderContactModule
}
